import { useEffect, useState } from 'react';
import { Edit2, Trash2, X, Eye, Code, Wand2 } from 'lucide-react';
import EmailEditor from '../components/EmailEditor';

interface Template {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  body_json?: any; // JSON do Unlayer Editor
  variables: string[] | null;
  created_at: string;
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showVisualEditor, setShowVisualEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body_html: '',
    body_json: null as any,
    variables: ''
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const url = selectedTemplate 
        ? `${apiUrl}/api/email/templates/${selectedTemplate.id}`
        : `${apiUrl}/api/email/templates`;
      
      const method = selectedTemplate ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        variables: formData.variables ? formData.variables.split(',').map(v => v.trim()) : []
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (data.success) {
        setShowModal(false);
        setSelectedTemplate(null);
        setFormData({ name: '', subject: '', body_html: '', body_json: null, variables: '' });
        loadTemplates();
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este template?')) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/email/templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (data.success) {
        loadTemplates();
      }
    } catch (error) {
      console.error('Erro ao deletar template:', error);
    }
  };

  const openEditModal = (template: Template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html,
      body_json: template.body_json || null,
      variables: template.variables ? template.variables.join(', ') : ''
    });
    setShowModal(true);
  };

  const openVisualEditor = (template?: Template) => {
    if (template) {
      setSelectedTemplate(template);
      setFormData({
        name: template.name,
        subject: template.subject,
        body_html: template.body_html,
        body_json: template.body_json || null,
        variables: template.variables ? template.variables.join(', ') : ''
      });
    } else {
      setSelectedTemplate(null);
      setFormData({ name: '', subject: '', body_html: '', body_json: null, variables: '' });
    }
    setShowVisualEditor(true);
  };

  const handleVisualEditorSave = (design: any, html: string) => {
    setFormData({ ...formData, body_json: design, body_html: html });
    setShowVisualEditor(false);
    setShowModal(true);
  };

  const openPreview = (template: Template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Templates de Email</h1>
          <p className="text-gray-600">Gerencie seus templates de email para campanhas</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => openVisualEditor()}
            className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600 flex items-center"
          >
            <Wand2 className="h-5 w-5 mr-2" />
            Editor Visual
          </button>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setFormData({ name: '', subject: '', body_html: '', body_json: null, variables: '' });
              setShowModal(true);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
          >
            <Code className="h-5 w-5 mr-2" />
            HTML Manual
          </button>
        </div>
      </div>

      {/* Lista de Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">Nenhum template encontrado</p>
          </div>
        ) : (
          templates.map(template => (
            <div key={template.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
              <p className="text-sm text-gray-600 mb-4">Assunto: {template.subject}</p>
              
              {template.variables && template.variables.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Variáveis:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.variables.map((v, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => openPreview(template)}
                  className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </button>
                <button
                  onClick={() => openEditModal(template)}
                  className="px-3 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="px-3 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{selectedTemplate ? 'Editar Template' : 'Novo Template'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Template *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assunto do Email *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variáveis (separadas por vírgula)
                  </label>
                  <input
                    type="text"
                    value={formData.variables}
                    onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Ex: nome, email, produto"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use no HTML como: {`{{nome}}, {{email}}`}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">HTML do Email *</label>
                  <textarea
                    value={formData.body_html}
                    onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                    rows={15}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {selectedTemplate ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Preview */}
      {showPreview && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Preview: {selectedTemplate.name}</h2>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="border border-gray-300 rounded-lg p-4">
              <div className="mb-4 pb-4 border-b">
                <p className="text-sm text-gray-600"><strong>Assunto:</strong> {selectedTemplate.subject}</p>
              </div>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedTemplate.body_html }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal do Editor Visual */}
      {showVisualEditor && (
        <div className="fixed inset-0 bg-white z-50">
          <EmailEditor
            initialDesign={formData.body_json || undefined}
            onSave={handleVisualEditorSave}
            onCancel={() => setShowVisualEditor(false)}
          />
        </div>
      )}
    </div>
  );
}
